import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      const errorMsg = formatApiError(err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 relative">
        <div className="w-full max-w-sm">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-cyan-400/60 hover:text-cyan-300 transition-colors mb-16"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-['Rajdhani'] tracking-wide mb-2">SIGN IN</h1>
              <p className="text-cyan-200/40">Enter your credentials to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Email</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Password</Label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="login-password"
                />
              </div>

              {error && (
                <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>SIGN IN <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            <p className="text-sm text-cyan-200/40 text-center">
              Don't have an account?{" "}
              <Link to="/register" className="text-cyan-400 hover:text-cyan-300" data-testid="register-link">
                Sign up
              </Link>
            </p>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-cyan-500/10" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#020a14] text-cyan-200/30">or</span></div>
            </div>

            <button
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full h-12 rounded-sm border border-cyan-500/20 hover:border-cyan-500/40 bg-cyan-500/[0.03] hover:bg-cyan-500/[0.06] transition-all flex items-center justify-center gap-3 text-cyan-200/70 hover:text-cyan-200 font-['Rajdhani'] tracking-wider text-sm"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              SIGN IN WITH GOOGLE
            </button>
          </div>
        </div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial" />
        <div className="text-center relative">
          <img src="/assets/logo.png" alt="MasteringMate" className="h-24 opacity-10 mx-auto" />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
